import { AnimatePresence, motion, Variants } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import { useMyMonitoringGetSiteList } from '@apps-common/apis/my-monitoring/site'
import { SiteSelectItem } from '@apps-common/apps/my-monitoring/pages/site/components/site-select'
import { ImageComponent } from '@apps-common/components/common'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ModalButton, MyMonitoringSiteSelectModal } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useGetDefaultSiteImage } from '@apps-common/hooks/useGetDefaultSiteImage'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { useMenuStore } from '@apps-common/stores'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'

const containerVariants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
      staggerChildren: 0.05,
      delayChildren: 0.2,
      staggerDirection: -1
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
      staggerChildren: 0.05,
      staggerDirection: 1
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
      staggerChildren: 0.05,
      delayChildren: 0.2,
      staggerDirection: -1
    }
  }
}

const SiteSelectSkeleton = () => (
  <div className='bg-surface rounded-2xl w-[260px] h-[45px] p-[5px] shadow-md animate-pulse'>
    <div className='flex items-center justify-between'>
      <div className='w-[33px] h-[33px] bg-skeleton rounded-xl' />
      <div className='h-[20px] w-[60%] bg-skeleton rounded-lg mr-2' />
    </div>
  </div>
)

interface IProps {
  site?: IMMSiteModel
  selectedSiteId?: string
  callback: (siteId: string) => void
  requestedSiteId?: string
  onRequestedSiteIdConsumed?: () => void
}

export const SiteSelect: React.FC<IProps> = ({
  site,
  selectedSiteId,
  callback,
  requestedSiteId,
  onRequestedSiteIdConsumed
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [buttonRect, setButtonRect] = useState<DOMRect>()
  const buttonContainerRef = useRef<HTMLDivElement>(null)
  const handledRequestedSiteIdRef = useRef<string | undefined>(undefined)
  const refetchedRequestedSiteIdRef = useRef<string | undefined>(undefined)
  const { open: menuOpen } = useMenuStore()
  const { isLoading: isLoadingSiteList, isError: isErrorSiteList, data: siteList, refetch: refetchSiteList } = useMyMonitoringGetSiteList({})
  const { loading: defaultImageLoading, defaultImageUrl } = useGetDefaultSiteImage(site?.siteId)

  useEffect(() => {
    setOpen(false)
  }, [menuOpen])

  useEffect(() => {
    setOpen(false)
    if (callback && site) {
      callback(site.unitId)
    }
  }, [callback, site])

  useEffect(() => {
    if (siteList && requestedSiteId && handledRequestedSiteIdRef.current !== requestedSiteId) {
      const requestedSite = siteList.find((s) => s.unitId === requestedSiteId)

      if (requestedSite && site?.unitId !== requestedSite.unitId) {
        handledRequestedSiteIdRef.current = requestedSiteId
        callback(requestedSite.unitId)
        onRequestedSiteIdConsumed?.()
        return
      }

      if (!requestedSite && siteList.length === 0 && refetchedRequestedSiteIdRef.current !== requestedSiteId) {
        refetchedRequestedSiteIdRef.current = requestedSiteId
        refetchSiteList()
        return
      }

      handledRequestedSiteIdRef.current = requestedSiteId
      onRequestedSiteIdConsumed?.()
    }

    if (selectedSiteId || site) return

    if (siteList && siteList.length > 0) {
      const defaultSite = siteList?.find((s) => s.isDefault)

      if (defaultSite) {
        callback(defaultSite?.unitId)
      } else {
        callback(siteList[0]?.unitId)
      }
    }
  }, [callback, onRequestedSiteIdConsumed, refetchSiteList, requestedSiteId, selectedSiteId, site, siteList])

  const loading = isLoadingSiteList || isErrorSiteList || defaultImageLoading || !site

  useEffect(() => {
    if (!open || loading) return

    const updateButtonRect = () => {
      const nextRect = buttonContainerRef.current?.getBoundingClientRect()

      if (nextRect) {
        setButtonRect(nextRect)
      }
    }

    updateButtonRect()
    window.addEventListener('resize', updateButtonRect)
    window.addEventListener('scroll', updateButtonRect, true)

    return () => {
      window.removeEventListener('resize', updateButtonRect)
      window.removeEventListener('scroll', updateButtonRect, true)
    }
  }, [open, loading])

  const toggleOpen = () => {
    if (!open) {
      const nextRect = buttonContainerRef.current?.getBoundingClientRect()

      if (nextRect) {
        setButtonRect(nextRect)
      }
    }

    setOpen(!open)
  }

  const selectedSiteButton = (
    <button
      type='button'
      aria-label='Selected Site'
      className='bg-surface backdrop-blur-md text-text p-[5px] rounded-2xl w-full text-right shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]'
      onClick={toggleOpen}
    >
      <div className='flex items-center justify-between gap-[10px] pr-[10px]'>
        <ImageComponent
          src={site?.siteImageUrl || defaultImageUrl}
          alt={site?.siteName || `Selected site ${site?.accountNo}`}
          imageClasses='min-w-[100%] min-h-[100%] object-cover'
          containerClasses='border border-border bg-surface rounded-xl overflow-hidden w-[35px] min-w-[35px] h-[35px] min-h-[35px]'
          errorIconClasses='ml-1 w-[35px] min-w-[35px] h-[35px] min-h-[35px]'
          ignoreContainerOnError
        />
        <div className='flex items-center justify-end gap-[10px]'>
          <div className='jost text-[0.8rem] leading-[1.1rem] line-clamp-2'>{site?.siteName || `Site - Acc# ${site?.accountNo}`}</div>
          <commonIcons.chevronDownSharp className='' />
        </div>
      </div>
    </button>
  )

  return (
    <>
      <ModalButton
        component={MyMonitoringSiteSelectModal}
        disabled={loading}
        classes='block sm:hidden !bg-transparent'
        componentProps={{
          selectedSite: site
        }}
        confirmCallback={(unitId: string) => callback(unitId)}
        enterAnimation={modalSlideUpEnterAnimation}
        leaveAnimation={modalSlideUpLeaveAnimation}
      >
        <ButtonRoundSm
          type='div'
          size='lg'
          className='bg-surface text-text !w-[35px] !h-[35px] shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]'
        >
          <commonIcons.stack className='text-[1.1rem]' />
        </ButtonRoundSm>
      </ModalButton>
      <div className='hidden sm:block'>
        <AnimatePresence>
          {open && !loading && (
            <>
              <motion.div
                key='site-select-open-overlay'
                {...motionFadeIn(0.2)}
                className='absolute top-0 left-0 z-[1] min-h-[100dvh] w-screen bg-bg/10 backdrop-blur-sm'
                onClick={() => setOpen(false)}
              />
              <motion.div
                key='site-select-open-list'
                variants={containerVariants}
                initial='initial'
                animate='animate'
                exit='exit'
                className='absolute z-[16] flex w-[260px] flex-col gap-[10px] rounded-3xl border border-border bg-surface/30 p-[10px] text-right shadow-sm backdrop-blur-md'
                style={{
                  top: buttonRect ? buttonRect.bottom + 10 : 85,
                  left: buttonRect?.left,
                  width: buttonRect?.width
                }}
              >
                {siteList?.map((s) => (
                  <SiteSelectItem
                    key={`site-select-item-${s.unitId}`}
                    selectedSite={site?.unitId}
                    site={s}
                    callback={(site) => {
                      callback(site?.unitId)
                      setOpen(false)
                    }}
                  />
                ))}
              </motion.div>
              {buttonRect && (
                <motion.div
                  key='site-select-open-button'
                  {...motionFadeIn(0.2)}
                  className='fixed z-[17]'
                  style={{
                    top: buttonRect.top,
                    left: buttonRect.left,
                    width: buttonRect.width
                  }}
                >
                  {selectedSiteButton}
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {loading && (
            <motion.div>
              <SiteSelectSkeleton />
            </motion.div>
          )}
          {!loading && (
            <>
              <motion.div ref={buttonContainerRef} className='w-[260px] relative z-[9]'>
                {selectedSiteButton}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
